/**
 * Manual redirects for legacy URLs, blog posts, and other custom redirects
 */
import { translateUrl } from "./url-slug-mapping";

const manualRedirects: Record<string, string> = {
  "/blog/bosw23": "/blog/meteoschweiz-best-of-swiss-web-2023",
  "/blog/lean-poker": "/blog/lean-poker-bots-all-in",
  "/blog/qhackathon": "/blog/code-retreat-mission-impossible",
  "/blog/wanderwochenende-2022": "/blog/spannung-spass-spiel-simmental",
  "/blog/wochenende-im-schnee": "/blog/quality-time-snow-weekend",
  "/data-protection": "/datenschutz",
  // Retired job openings. Principal and Platform Engineer were posted in February
  // 2024 and describe a Quatico that no longer exists — micro-frontend portals and
  // a low-code product unit, a business the Neuausrichtung ended. /jobs is an SSG
  // route, so both stay Single-Hop. The English routes live under /en/careers/*
  // (not /en/jobs/*) and resolve to /en/careers, which is the same JobsPage.
  //
  // /jobs/senior-software-engineer steht bewusst NICHT in dieser Liste: Der Slug
  // ist neu belegt. Die Rolle wurde für das hybride Projektteam neu geschnitten
  // (Menschen führen, Agenten liefern zu) und ist wieder eine echte Seite — ein
  // Redirect würde sie abfangen.
  "/jobs/principal-software-engineer": "/jobs",
  "/jobs/platform-engineer": "/jobs",
  "/en/careers/principal-software-engineer": "/en/careers",
  "/en/careers/platform-engineer": "/en/careers",
  // Short, campaign-brandable URL for the KI-Technologie LinkedIn series CTA.
  "/ki-ansatz": "/dienstleistungen/ki-technologie",
  // Die beiden Beratungsseiten sind am 11.9.2026 unter /ki-anwendungen gezogen und
  // haben dabei das doppelte «ki» im Kindsegment verloren — das Elternsegment traegt
  // es bereits. Vier Quellen, alle Single-Hop:
  //
  //   - /dienstleistungen/ki-risiken war seit 2.9.2026 auf PROD und ist indexiert.
  //     Dieser Eintrag ist der einzige, der bestehende externe Links traegt.
  //   - /ki-anwendungen/ki-potenzial wurde mit PR #860 gemergt, aber nie released.
  //     Der Eintrag kostet nichts und faengt die Links aus dem PR-Review ab.
  //   - /ki-risiken und /ki-potenzial sind kurze Einstiegs-URLs fuer Kampagnen,
  //     nach dem Muster von /ki-ansatz: eine 301 auf die lange URL, KEINE zweite
  //     Live-Route. Zwei Routen mit demselben Inhalt braeuchten canonical-Tags,
  //     die es hier nicht gibt, und Google wertete beide ab.
  //
  // Der EN-Pfad /en/services/ai-risks ist ebenfalls auf PROD und liefert 200 —
  // nachgemessen 11.9.2026. Die Slug-Mapping-Aenderung allein liesse ihn ins Leere
  // laufen, deshalb steht er hier. /en/ai-applications/ai-potential dagegen ist 404
  // (nie released) und braucht keinen Eintrag; er steht trotzdem da, weil die
  // Review-Links aus PR #860 darauf zeigen und nichts kosten.
  "/dienstleistungen/ki-risiken": "/angebote/ki-anwendungen/risiken",
  "/ki-anwendungen/ki-potenzial": "/angebote/ki-anwendungen/potenzial",
  "/ki-risiken": "/angebote/ki-anwendungen/risiken",
  "/ki-potenzial": "/angebote/ki-anwendungen/potenzial",
  "/en/services/ai-risks": "/en/offerings/ai-applications/risks",
  "/en/ai-applications/ai-potential": "/en/offerings/ai-applications/potential",
  // /ki-anwendungen zieht unter /angebote (18.9.2026, Entscheid Jan). Die Seite ist
  // eine der drei Angebots-Detailseiten und lag als einzige nicht unter /angebote/ —
  // deshalb trug sie als einzige keinen Rueckweg «Zurueck zu Angebote». Zwoelf
  // Quellen ziehen mit: die Uebersicht und ihre fuenf Unterseiten, je DE und EN.
  // Alle zwoelf antworteten vor dem Umzug mit 200 (nachgemessen 18.9.2026), also
  // traegt jede bestehende Links und Index-Eintraege. Dauerhaft, ohne Ablauffrist.
  //
  // Die Ziele der AELTEREN Eintraege oben und weiter unten sind mitgewandert: Ein
  // Eintrag, dessen Ziel selbst 301t, kostet einen zweiten Hop und verliert Ranking.
  "/ki-anwendungen": "/angebote/ki-anwendungen",
  "/ki-anwendungen/risiken": "/angebote/ki-anwendungen/risiken",
  "/ki-anwendungen/potenzial": "/angebote/ki-anwendungen/potenzial",
  "/ki-anwendungen/offerten-generator":
    "/angebote/ki-anwendungen/offerten-generator",
  "/ki-anwendungen/assistenten": "/angebote/ki-anwendungen/assistenten",
  "/ki-anwendungen/document-as-a-service":
    "/angebote/ki-anwendungen/document-as-a-service",
  "/en/ai-applications": "/en/offerings/ai-applications",
  "/en/ai-applications/risks": "/en/offerings/ai-applications/risks",
  "/en/ai-applications/potential": "/en/offerings/ai-applications/potential",
  "/en/ai-applications/quote-generator":
    "/en/offerings/ai-applications/quote-generator",
  "/en/ai-applications/assistants": "/en/offerings/ai-applications/assistants",
  "/en/ai-applications/document-as-a-service":
    "/en/offerings/ai-applications/document-as-a-service",
  // Ausschreibungen zieht unter den Sammelpfad /kampagne (15.10.2026, QUAWEB-40,
  // Entscheid Jan). Die Mailsequenz rfp-2026-09 verlinkt die alte URL mit
  // `user_id` und `utm_*`; die 301 behaelt den Query-String (seit #943).
  // /umfrage-unternehmen/ausschreibungen bleibt 404 (entschieden 23.9.2026) und
  // steht deshalb nicht hier.
  "/angebote/ki-anwendungen/ausschreibungen": "/kampagne/ausschreibungen",
  "/en/offerings/ai-applications/tenders": "/en/campaign/tenders",
  // Retired SaaS legal URLs (localized platform paths) → consolidated DE pages.
  // Kept as exact matches so the nested /plateforme|/piattaforma legal URLs
  // resolve to /agb|/datenschutz rather than /plattform/agb via the generic
  // locale rule below.
  "/fr/cgv": "/agb",
  "/fr/confidentialite": "/datenschutz",
  "/it/cgc": "/agb",
  "/it/privacy": "/datenschutz",
  "/fr/plateforme/cgv": "/agb",
  "/fr/plateforme/confidentialite": "/datenschutz",
  "/it/piattaforma/cgc": "/agb",
  "/it/piattaforma/privacy": "/datenschutz",
  "/platform/agb": "/agb",
  "/platform/datenschutz": "/datenschutz",
  "/platform/fr/agb": "/agb",
  "/platform/fr/datenschutz": "/datenschutz",
  "/platform/it/agb": "/agb",
  "/platform/it/datenschutz": "/datenschutz",
  "/erfolgsgeschichte/ewz-kundenportal": "/success-stories/ewz",
  "/erfolgsgeschichte/far-geschaeftsprozesse": "/success-stories/far",
  "/erfolgsgeschichte/meteoschweiz-webportal": "/success-stories/meteoschweiz",
  "/module/analytics-reporting": "/module/analytics",
  // Retired /kunden, superseded by /referenzen (Register 28).
  //
  // Register entry 18 (18.7.2026) decided «/kunden bleibt (additiv, kein
  // Redirect)» — at that time /referenzen ADDED to /kunden. Since 11.8.2026 it
  // carries ALL customers in one list (entry 23), so the addition became a
  // replacement and the July decision is superseded.
  //
  // Found by seiten-erreichbar.spec.ts: in the sitemap at priority 0.8, not a
  // single inbound link. The one thing only /kunden had — the named ewz
  // testimonial — moved to /referenzen first (preserve-then-delete).
  //
  // EN resolves to the German canonical, the established pattern for the retired
  // module pages below.
  "/kunden": "/referenzen",
  "/en/customers": "/referenzen",
  // Retired IAM/Profile-Management module pages, absorbed into /plattform
  // (Etappe 2, Register 16). EN routes resolve to the German /plattform.
  "/module/iam": "/plattform",
  "/en/modules/iam": "/plattform",
  "/module/profile-management": "/plattform",
  "/en/modules/profile-management": "/plattform",
  // Explicit FR/IT keys for the same retired modules. The generic locale rule only does a
  // single translateUrl pass (/fr/modules/iam → /module/iam) and does NOT re-run these
  // manualRedirects, so without direct keys the localized URLs would be a 2-hop chain that
  // validate:redirects:chains is blind to. Direct single-hop, same pattern as VP-starter.
  "/fr/modules/iam": "/plattform",
  "/it/moduli/iam": "/plattform",
  "/fr/modules/profile-management": "/plattform",
  "/it/moduli/profile-management": "/plattform",
  "/platform": "/plattform",
  "/platform/fr": "/plattform",
  "/platform/fr/produit": "/plattform",
  "/platform/fr/technologie": "/plattform",
  "/platform/it": "/plattform",
  "/platform/it/prodotto": "/plattform",
  "/platform/it/tecnologia": "/plattform",
  "/platform/produkt": "/plattform",
  "/platform/technologie": "/plattform",
  // Sub-pages /platform/product + /platform/technology (the old "Digitale Revolution"
  // product narrative) retired with the /plattform overhaul (Phase-3 Eckpfeiler 2). The
  // English routes were the live ones (App.tsx); the German/FR/IT keys above already
  // existed. All four resolve to the reborn /plattform page.
  "/platform/product": "/plattform",
  "/platform/technology": "/plattform",
  "/en/platform/product": "/plattform",
  "/en/platform/technology": "/plattform",
  // Loesungs-Seiten (Alt-Bestand «Enterprise Portal»/CDS-Portal), abgebaut 11.8.2026.
  // Ihre Substanz ist mit Eckpfeiler 2 nach /plattform gewandert (Eintrag 16,
  // Preserve-then-Delete, PR #493/#494); die Seiten selbst waren seit dem Relaunch
  // von KEINER Seite mehr verlinkt — gebaut, gepflegt, unerreichbar.
  // Ziel ist /plattform, weil dort die Modul- und Portal-Substanz heute lebt.
  "/loesungen/vertriebs-und-auftragsverwaltung": "/plattform",
  "/loesungen/digitaler-kundendienst": "/plattform",
  "/loesungen/custom-cds-portal": "/plattform",
  "/en/solutions/sales-and-order-management": "/en/platform",
  "/en/solutions/digital-customer-service": "/en/platform",
  "/en/solutions/custom-cds-portal": "/en/platform",
  // Branchen-Seiten Maschinenbau + Energie (VP-Alt, «Enterprise Portal»-Erzaehlung),
  // abgebaut 11.8.2026. Nicht zu verwechseln mit /angebote/branche/:slug — das sind
  // die fuenf neu getexteten Nachfolger am Offerten-Generator.
  // Ziel ist /referenzen: dort steht der Branchen-Absatz («von der Energiewirtschaft
  // bis zum Maschinenbau», referenzen.mdx:21), der aus Eintrag 7 hervorging.
  "/branchen/maschinenbau": "/referenzen",
  "/branchen/energie": "/referenzen",
  "/en/industries/maschinenbau": "/en/references",
  "/en/industries/energie": "/en/references",
  // Angebote — System Engineering (Relaunch Phase 3). The service pages
  // fachliche-individualisierung + integration-consulting were retired, first
  // consolidated into /angebote/individualsoftware and now re-narrated into
  // /angebote/system-engineering. /angebote/individualsoftware itself is now a
  // redirect (below), so every legacy key is repointed SINGLE-HOP at the new page
  // (D14/D2 — never chain via the old URL). The EN slug functional-customization
  // is asymmetric, so it is listed explicitly.
  //
  // The English page /en/offerings/system-engineering shipped in Etappe 3 (#501), so
  // the EN sources now resolve to it directly (the interim cross-locale target
  // /angebote/system-engineering is retired). Both /en/offerings/system-engineering and
  // /angebote/system-engineering are built SSG routes, so every key stays Single-Hop and
  // passes validate:redirects:ssg.
  "/dienstleistungen/fachliche-individualisierung":
    "/angebote/system-engineering",
  "/dienstleistungen/integration-consulting": "/angebote/system-engineering",
  "/en/services/functional-customization": "/en/offerings/system-engineering",
  "/en/services/integration-consulting": "/en/offerings/system-engineering",
  // The six /services/… legacy entrypoints pointed at the now-retired DE URLs. Repoint
  // each directly at its real successor so they stay Single-Hop (not /services/… →
  // /dienstleistungen/… → /angebote/…). The four BDD-workshop entrypoints now reach their
  // own /angebote/* pages (built in Etappe 2b); the two generic ones stay on
  // system-engineering, which absorbed the fachliche-Individualisierung narrative.
  "/services/fachliche-individualisierung": "/angebote/system-engineering",
  "/services/fachliche-individualisierung/feasibility-workshop": "/angebote/feasibility-workshop",
  "/services/fachliche-individualisierung/go-to-market-test": "/angebote/go-to-market-test",
  "/services/fachliche-individualisierung/mission-analysis": "/angebote/mission-analysis",
  "/services/fachliche-individualisierung/product-scale-up": "/angebote/product-scale-up",
  "/services/integration-consulting": "/angebote/system-engineering",
  // /angebote/individualsoftware itself is retired and re-narrated into
  // /angebote/system-engineering (Relaunch Phase 3). Both language variants 301
  // Single-Hop at their own-language successor: the DE source at the DE page, the EN
  // source at the EN page /en/offerings/system-engineering (shipped in Etappe 3, #501).
  "/angebote/individualsoftware": "/angebote/system-engineering",
  "/en/offerings/custom-software": "/en/offerings/system-engineering",
  // Workshop-Reihe — migrated from /dienstleistungen/* to /angebote/* (Phase-3 Etappe
  // 5d+5e). Same pages, new URL. EN slugs unchanged (ai-potential-analysis /
  // ai-agent-workshop), only the parent moves to /en/offerings. All Single-Hop.
  "/dienstleistungen/ki-potenzialanalyse": "/angebote/ki-potenzialanalyse",
  "/en/services/ai-potential-analysis": "/en/offerings/ai-potential-analysis",
  "/dienstleistungen/ki-agenten-workshop": "/angebote/ki-agenten-workshop",
  "/en/services/ai-agent-workshop": "/en/offerings/ai-agent-workshop",
  "/vertriebsportal": "/angebote/ki-anwendungen/offerten-generator",
  "/produkte/starter": "/angebote/ki-anwendungen/offerten-generator",
  "/produkte/best": "/angebote/ki-anwendungen/offerten-generator",
  "/produkte/enterprise": "/angebote/ki-anwendungen/offerten-generator",
  "/produkte/vertriebsportal-enterprise": "/angebote/ki-anwendungen/offerten-generator",
  "/produkte/vertriebsportal-enterprise-hubspot":
    "/angebote/ki-anwendungen/offerten-generator",
  "/produkte/vertriebsportal-starter/dienstleistungs-katalog":"/angebote",
  "/produkte/vertriebsportal-starter/playground":
    "/angebote/ki-anwendungen/offerten-generator",
  // Phase-3 Etappe 2: the playground migrated to the Offerten-Generator page; the old
  // VP playground URLs and the superseded KiGenerator URLs redirect to the new page.
  // DE playground → DE page; EN playground / EN KiGenerator → EN page. Single-hop.
  "/produkte/vertriebsportal/playground": "/angebote/ki-anwendungen/offerten-generator",
  "/produkte/vertriebsportal/en/playground":
    "/en/offerings/ai-applications/quote-generator",
  "/en/products/sales-portal-starter/playground":
    "/en/offerings/ai-applications/quote-generator",
  // Ohne «-starter» — die Variante steht LIVE als eigene Seite (HTTP 200,
  // nachgemessen 13.8.2026 an www.quatico.com) und fehlte hier. Gefunden von
  // `check:alt-urls`, das die Sitemap der alten Site gegen den Neubau haelt;
  // die drei bestehenden Redirect-Waechter sahen sie nicht, weil sie ueber
  // `redirectMapping` iterieren — was nicht eingetragen ist, pruefen sie nicht.
  "/en/products/sales-portal/playground":
    "/en/offerings/ai-applications/quote-generator",
  "/vertriebsportal/ki-dienstleistungs-konfigurator-generator":
    "/angebote/ki-anwendungen/offerten-generator",
  "/en/sales-portal/ai-service-configurator-generator":
    "/en/offerings/ai-applications/quote-generator",
  // VP signup landing pages retired (the product is stopped). All konto-erstellen /
  // create-account routes 301 permanently to the Offerten-Generator successor (§5 Z.148).
  // The enterprise-hubspot key points DIRECTLY at the final target — not at the
  // professional konto-erstellen route — to keep the chain single-hop.
  "/produkte/vertriebsportal-professional/konto-erstellen":
    "/angebote/ki-anwendungen/offerten-generator",
  "/en/products/sales-portal-professional/create-account":
    "/en/offerings/ai-applications/quote-generator",
  "/produkte/vertriebsportal-enterprise-hubspot/konto-erstellen":
    "/angebote/ki-anwendungen/offerten-generator",
  "/vertriebsportal-enterprise": "/angebote/ki-anwendungen/offerten-generator",
  "/vertriebsportal/branche/facility-services": "/angebote/branche/facility-services",
  "/vertriebsportal/branche/gebaeudetechnik": "/angebote/branche/gebaeudetechnik",
  "/vertriebsportal/branche/reinigungsunternehmen": "/angebote/branche/reinigungsunternehmen",
  "/vertriebsportal/branche/treuhand-gesellschaft": "/angebote/branche/treuhand-gesellschaft",
  "/vertriebsportal/branche/treuhand-service": "/angebote/branche/treuhand-service",
  "/vertriebsportal/features": "/angebote/ki-anwendungen/offerten-generator",
  // Legacy localized VP-starter URLs (French/Italian versions retired) → DE pages
  "/vertriebsportal/individualisierung": "/angebote/ki-anwendungen/offerten-generator",
  "/vertriebsportal/win": "/angebote/ki-anwendungen/offerten-generator",
  "/produkte/vertriebsportal/branche/treuhand-gesellschaft": "/angebote/branche/treuhand-gesellschaft",
  "/produkte/vertriebsportal/en/branche/treuhand-gesellschaft": "/en/offerings/industry/fiduciary-company",
  "/en/products/sales-portal/industry/fiduciary-company": "/en/offerings/industry/fiduciary-company",
  "/produkte/vertriebsportal/branche/treuhand-service": "/angebote/branche/treuhand-service",
  "/produkte/vertriebsportal/en/branche/treuhand-service": "/en/offerings/industry/fiduciary-service",
  "/en/products/sales-portal/industry/fiduciary-service": "/en/offerings/industry/fiduciary-service",
  "/produkte/vertriebsportal/branche/gebaeudetechnik": "/angebote/branche/gebaeudetechnik",
  "/produkte/vertriebsportal/en/branche/gebaeudetechnik": "/en/offerings/industry/building-technology",
  "/en/products/sales-portal/industry/building-technology": "/en/offerings/industry/building-technology",
  "/produkte/vertriebsportal/branche/reinigungsunternehmen": "/angebote/branche/reinigungsunternehmen",
  "/produkte/vertriebsportal/en/branche/reinigungsunternehmen": "/en/offerings/industry/cleaning-company",
  "/en/products/sales-portal/industry/cleaning-company": "/en/offerings/industry/cleaning-company",
  "/produkte/vertriebsportal/branche/facility-services": "/angebote/branche/facility-services",
  "/produkte/vertriebsportal/en/branche/facility-services": "/en/offerings/industry/facility-services",
  "/en/products/sales-portal/industry/facility-services": "/en/offerings/industry/facility-services",
  "/produkte/vertriebsportal": "/angebote/ki-anwendungen/offerten-generator",
  "/produkte/vertriebsportal/en": "/en/offerings/ai-applications/quote-generator",
  "/en/products/sales-portal": "/en/offerings/ai-applications/quote-generator",
  "/produkte/vertriebsportal-starter": "/angebote/ki-anwendungen/offerten-generator",
  "/produkte/vertriebsportal-starter/en": "/en/offerings/ai-applications/quote-generator",
  "/en/products/sales-portal-starter": "/en/offerings/ai-applications/quote-generator",
  "/produkte/vertriebsportal-starter/features": "/angebote/ki-anwendungen/offerten-generator",
  "/produkte/vertriebsportal-starter/en/features": "/en/offerings/ai-applications/quote-generator",
  "/en/products/sales-portal-starter/features": "/en/offerings/ai-applications/quote-generator",
  "/produkte/vertriebsportal-business": "/angebote/ki-anwendungen/offerten-generator",
  "/produkte/vertriebsportal-business/en": "/en/offerings/ai-applications/quote-generator",
  "/en/products/sales-portal-business": "/en/offerings/ai-applications/quote-generator",
  "/produkte/vertriebsportal-professional": "/angebote/ki-anwendungen/offerten-generator",
  "/produkte/vertriebsportal-professional/en": "/en/offerings/ai-applications/quote-generator",
  "/en/products/sales-portal-professional": "/en/offerings/ai-applications/quote-generator",
  "/produkte/vertriebsportal/dienstleistungs-katalog": "/angebote",
  "/produkte/vertriebsportal/en/dienstleistungs-katalog": "/en/offerings",
  "/vertriebsportal/dienstleistungs-katalog": "/angebote",
  "/en/products/sales-portal/service-catalog": "/en/offerings",
  // Ohne «products/» — dieselbe Sorte Luecke wie beim EN-Playground darueber:
  // live eine eigene Seite (HTTP 200, 13.8.2026), im Mapping nur die
  // Nachbarvariante. Zwei von 122 Alt-URLs waren so unversorgt.
  "/en/sales-portal/service-catalog": "/en/offerings",
  "/fr/produits/portail-de-vente-starter": "/angebote/ki-anwendungen/offerten-generator",
  "/it/prodotti/portale-di-vendita-starter": "/angebote/ki-anwendungen/offerten-generator",
  "/fr/produits/portail-de-vente-enterprise": "/angebote/ki-anwendungen/offerten-generator",
  "/it/prodotti/portale-di-vendita-enterprise": "/angebote/ki-anwendungen/offerten-generator",
  "/fr/produits/portail-de-vente-enterprise-hubspot": "/angebote/ki-anwendungen/offerten-generator",
  "/it/prodotti/portale-di-vendita-enterprise-hubspot": "/angebote/ki-anwendungen/offerten-generator",
  // Das Whitepaper gibt es seit dem 9.10.2026 nur gegen eine E-Mail-Adresse (Entscheid
  // Jan): Das PDF liegt in packages/server/assets/ und geht als Anhang mit. Die alte
  // oeffentliche Adresse haengt in LinkedIn-Beitraegen und Newslettern und landet am
  // Formular statt auf einem 404.
  "/static/content/whitepaper-smart-client-relations-quatico-solutions.pdf":
    "/smart-client-relations#whitepaper",
};

/**
 * Legacy VP edition branch URL prefixes. A slug under one of them resolves through
 * the exact entry `/produkte/vertriebsportal/branche/<slug>` in `manualRedirects`;
 * those entries are the list of known branch slugs. A slug without an entry has no
 * branch page and redirects to the overview `/angebote` (QUAWEB-43). Checked after
 * exact `manualRedirects` matches.
 */
const vpBranchEditionPrefixes: ReadonlyArray<string> = [
  "/produkte/vertriebsportal-starter/branche/",
  "/produkte/vertriebsportal-business/branche/",
];

const VP_BRANCH_UNIFIED_PREFIX = "/produkte/vertriebsportal/branche/";
const VP_BRANCH_UNKNOWN_SLUG_TARGET = "/angebote";

/**
 * Retired French and Italian URL prefixes. The FR/IT site versions have been
 * removed; their URLs are 301-redirected to the German equivalent, derived by
 * translating each path segment back to its German slug.
 */
const LEGACY_LOCALE_PREFIXES = ["/fr", "/it"] as const;

/**
 * Paths that were removed without any German equivalent and must return
 * 410 Gone instead of a redirect.
 *
 * Currently empty: every retired French/Italian URL maps to a German page and
 * is 301-redirected. Add paths here for content removed without a replacement.
 */
const gonePaths: ReadonlySet<string> = new Set<string>([]);

/**
 * Append trailing slashes to relative URLs only. A fragment stays behind the
 * slash: `/a#b` becomes `/a/#b`, not `/a#b/`.
 */
const appendTrailingSlash = (url: string): string => {
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  const hashIndex = url.indexOf("#");
  const pathPart = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const fragment = hashIndex === -1 ? "" : url.slice(hashIndex);
  return `${pathPart.endsWith("/") ? pathPart : `${pathPart}/`}${fragment}`;
};

/**
 * The comparison convention: slash on the wire, slashless when looking up.
 *
 * `appendTrailingSlash` above adds the slash for Express' 301 `Location`. Anything that
 * *compares* a path — route lookup, `gonePaths`, the build-time validators — normalizes
 * with this first. Comparing a slashed target against a slashless set never matches;
 * that is what made `validate:redirects:ssg` report 0 of 76 valid for four months.
 */
export const normalizePathname = (pathname: string): string =>
  pathname !== "/" && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;

const stripQueryAndHash = (rawPath: string): string =>
  rawPath.split("?")[0].split("#")[0];

/**
 * The query string of a request URL, with its `?`, or `""`. A request fragment
 * ends the query and is not part of it.
 */
export const queryStringOf = (rawUrl: string): string => {
  const queryIndex = rawUrl.indexOf("?");
  if (queryIndex === -1) {
    return "";
  }
  return rawUrl.slice(queryIndex).split("#")[0];
};

/**
 * Append a request's query string to a redirect target. A fragment in the
 * target stays last: `/pfad/#anker` with `?a=1` becomes `/pfad/?a=1#anker`.
 *
 * No target in `manualRedirects` carries its own `?`, so there is no merge
 * rule. Campaign links carry `utm_*` and `user_id`; without this the 301 drops
 * them before `HubSpotIdentifyFromUrl` and the campaign attribution read them.
 */
export const appendQueryString = (target: string, search: string): string => {
  const query = search.startsWith("?") ? search.slice(1) : search;
  if (query === "") {
    return target;
  }
  const hashIndex = target.indexOf("#");
  const pathPart = hashIndex === -1 ? target : target.slice(0, hashIndex);
  const fragment = hashIndex === -1 ? "" : target.slice(hashIndex);
  return `${pathPart}?${query}${fragment}`;
};

/**
 * Translate a retired French/Italian URL to its German equivalent, or `undefined`
 * if the path is not under a legacy locale prefix.
 */
const resolveLegacyLocaleRedirect = (path: string): string | undefined => {
  const sourceLocale = LEGACY_LOCALE_PREFIXES.find(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
  if (!sourceLocale) {
    return undefined;
  }
  const locale = sourceLocale === "/it" ? "it" : "fr";
  const germanPath = translateUrl(path, locale, "de");
  return appendTrailingSlash(germanPath);
};

const resolveTargetForPath = (path: string): string | undefined => {
  const exact = manualRedirects[path];
  if (exact) {
    return appendTrailingSlash(exact);
  }

  for (const prefix of vpBranchEditionPrefixes) {
    if (path.startsWith(prefix) && path.length > prefix.length) {
      const unifiedPath = VP_BRANCH_UNIFIED_PREFIX + path.slice(prefix.length);
      const knownTarget = Object.hasOwn(manualRedirects, unifiedPath)
        ? manualRedirects[unifiedPath]
        : VP_BRANCH_UNKNOWN_SLUG_TARGET;
      return appendTrailingSlash(knownTarget);
    }
  }

  return resolveLegacyLocaleRedirect(path);
};

/**
 * Resolve a request path to a redirect target (relative URL with trailing slash),
 * or `undefined` if no redirect applies. Matching ignores the query string; the
 * target keeps it (see `appendQueryString`). A request fragment is dropped.
 */
export const resolveRedirectPath = (rawPath: string): string | undefined => {
  const target = resolveTargetForPath(
    normalizePathname(stripQueryAndHash(rawPath)),
  );
  return target === undefined
    ? undefined
    : appendQueryString(target, queryStringOf(rawPath));
};

/**
 * Whether a request path should return 410 Gone (content removed without a
 * German equivalent). Checked only after `resolveRedirectPath` returns no match.
 */
export const isGonePath = (rawPath: string): boolean => {
  return gonePaths.has(normalizePathname(stripQueryAndHash(rawPath)));
};

/**
 * Export redirect mapping
 *
 * Note: We no longer generate automatic localization redirects because all
 * active routes are defined directly in App.tsx. Retired French/Italian URLs
 * are handled by `resolveRedirectPath` (generic locale rule), not listed here
 * as static keys. Prefix-based branch consolidation is likewise handled there.
 */
export const redirectMapping: Record<string, string> = Object.fromEntries(
  Object.entries(manualRedirects).map(([key, value]) => [
    key,
    appendTrailingSlash(value),
  ]),
);

/** ESM default for cross-package imports (e.g. website `import x from "../server/…"`). */
export default {
  appendQueryString,
  queryStringOf,
  redirectMapping,
  resolveRedirectPath,
  isGonePath,
  normalizePathname,
};
